import { useCallback, useRef, useState } from "react";
import { fetchWithAuth } from "@/lib/utils";

export type SlackInstallStatus =
  | { state: "not_connected" | "ambiguous" }
  | { state: "connected"; workspaceId: string; installedAt: string; updatedAt: string; scopes: string[] };

export interface SlackInstallLink {
  tenantId: string;
  installUrl: string;
  expiresAt: string;
}

export type SlackInstallError = "tenant_not_found" | "oauth_not_configured" | "forbidden" | "unsupported" | "failed";

async function request<T>(url: string, getToken: () => Promise<string>): Promise<T> {
  const response = await fetchWithAuth(url, { headers: { Accept: "application/json" }, cache: "no-store" }, getToken);
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message = typeof body === "object" && body !== null && "error" in body ? body.error : null;
    let reason: SlackInstallError = "failed";
    if (response.status === 404 && message === "Tenant not found") reason = "tenant_not_found";
    else if ([404, 405, 501].includes(response.status)) reason = "unsupported";
    else if (response.status === 503 && message === "Slack OAuth is not configured") reason = "oauth_not_configured";
    else if (response.status === 401 || response.status === 403) reason = "forbidden";
    throw new SlackInstallRequestError(reason);
  }
  try {
    return await response.json() as T;
  } catch {
    throw new SlackInstallRequestError("failed");
  }
}

export class SlackInstallRequestError extends Error {
  constructor(readonly reason: SlackInstallError) {
    super(reason);
  }
}

export function useSlackInstall(getToken: () => Promise<string>) {
  const [status, setStatus] = useState<SlackInstallStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<SlackInstallError | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState<SlackInstallError | null>(null);
  const statusInFlight = useRef(false);
  const linkInFlight = useRef(false);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const fetchStatus = useCallback(async () => {
    if (statusInFlight.current) return;
    statusInFlight.current = true;
    setStatusLoading(true);
    setStatusError(null);
    setStatus(null);
    try {
      const result = await request<SlackInstallStatus>("/api/admin/slack/install-status", getTokenRef.current);
      if (!["connected", "not_connected", "ambiguous"].includes(result?.state)) throw new SlackInstallRequestError("failed");
      setStatus(result);
    } catch (error) {
      setStatusError(error instanceof SlackInstallRequestError ? error.reason : "failed");
    } finally {
      statusInFlight.current = false;
      setStatusLoading(false);
    }
  }, []);

  const generateLink = useCallback(async (): Promise<string | null> => {
    if (linkInFlight.current) return null;
    linkInFlight.current = true;
    setLinkLoading(true);
    setLinkError(null);
    try {
      const result = await request<SlackInstallLink>("/api/admin/slack/install-link", getTokenRef.current);
      if (typeof result?.installUrl !== "string" || !result.installUrl.trim()
        || typeof result.expiresAt !== "string" || !Number.isFinite(Date.parse(result.expiresAt))) {
        throw new SlackInstallRequestError("failed");
      }
      return result.installUrl;
    } catch (error) {
      setLinkError(error instanceof SlackInstallRequestError ? error.reason : "failed");
      return null;
    } finally {
      linkInFlight.current = false;
      setLinkLoading(false);
    }
  }, []);

  return { status, statusLoading, statusError, fetchStatus, linkLoading, linkError, generateLink };
}
